// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import axios from "axios";
import { Sha256 } from "@aws-crypto/sha256-js";
import { SignatureV4 } from "@smithy/signature-v4";
import { useCreatePOA } from "./useCreatePOA";
import { useCreateUser } from "./useCreateUser";
import { useGetFetchPOAText } from "./useFetchPOAText";
import { useGetLoginStatus } from "./useGetLoginStatus";
import { useGetSignStatus } from "./useGetSignStatus";
import { useSignPOA } from "./useSignPOA";
import { useStartLogin } from "./useStartLogin";
import { useStartSignIn } from "./useCustomerSignIn";
import { useStartSignUp } from "./useCustomerSignUp";
import { usePageViewTracking } from "./usePageviewTracking";
import { useCreateBooking } from "./useCreateBooking";
import { useGetBookingStatus } from "./useGetBookingStatus";

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
});

/**
 * Method to create a signed request for AWS API Gateway
 * @param {*} method GET/POST/PATCH/PUT/DELETE
 * @param {*} endpoint URL of endpoint including query parameters
 * @param {*} credentials AWS Credentials obtained from STS/Cognito
 * ```javascript
 * credentials: {
 *      accessKeyId,
 *    secretAccessKey,
 *    sessionToken,
 *    expiration,
 * }
 * ```
 * @param {*} headers Optional Headers to include in the request (JSON object)
 * ```javascript
 * headers: {
 *  "content-type": "application/json",
 * }
 * ```
 * @param {*} payload Optional payload to include in the request (JSON object)
 * @param {*} region Optional AWS region of the API Gateway, default eu-west-1
 * @param {*} service Optional AWS service name, default execute-api
 * @returns
 */
const signApiRequest = async (
  method,
  endpoint,
  credentials,
  headers = {},
  payload = null,
  region = "eu-west-1",
  service = "execute-api"
) => {
  const url = new URL(endpoint);
  let sortedQueryObject;
  let sortedQueryString;

  try {
    // Parse query parameters into an object and sort them alphabetically
    if (url.search?.length > 0) {
      const queryObject = url.search
        .replace("?", "")
        .toString()
        .split("&")
        .filter((param) => param.includes("="))
        .reduce((acc, param) => {
          const [key, value] = param.split("=");
          acc[key] = value;
          return acc;
        }, {});

      sortedQueryObject = Object.keys(queryObject)
        .sort()
        .reduce((acc, key) => {
          acc[key] = queryObject[key];
          return acc;
        }, {});

      sortedQueryString = Object.keys(sortedQueryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(sortedQueryObject[key])}`
        )
        .join("&");

      // Update the URL with the sorted query string
      url.search = sortedQueryString;
    }

    // Parse headers into an object
    let headerObject;
    if (typeof headers === "string") {
      headerObject = headers
        .split("\n")
        .filter((header) => header.includes(":"))
        .reduce((acc, header) => {
          const [key, value] = header.split(":");
          acc[key.trim()] = value.trim();
          return acc;
        }, {});
    } else {
      headerObject = headers;
    }

    // Add host to the header object
    headerObject.host = url.hostname;

    // Sort the header object alphabetically
    const sortedHeaderObject = Object.keys(headerObject)
      .sort()
      .reduce((acc, key) => {
        acc[key] = headerObject[key];
        return acc;
      }, {});

    // Prepare the request object
    const requestToSign = {
      method,
      protocol: url.protocol,
      headers: sortedHeaderObject,
      path: url.pathname,
      body: payload ? JSON.stringify(payload) : "",
      ...(sortedQueryObject && { query: sortedQueryObject }),
    };

    // Create the SignatureV4 signer
    const signer = new SignatureV4({
      credentials,
      region,
      service,
      sha256: Sha256,
    });

    // Sign the request
    const signedRequest = await signer.sign(requestToSign);

    return {
      endpoint: url.href,
      options: {
        method: signedRequest.method,
        headers: signedRequest.headers,
        body: signedRequest.body || null,
      },
    };
  } catch (error) {
    console.error("Error signing the request:", error);
    throw error;
  }
};

export {
  apiClient,
  signApiRequest,
  useCreateBooking,
  useCreatePOA,
  useCreateUser,
  useGetBookingStatus,
  useGetFetchPOAText,
  useGetLoginStatus,
  useGetSignStatus,
  usePageViewTracking,
  useSignPOA,
  useStartLogin,
  useStartSignIn,
  useStartSignUp,
};
